import { CreateBlockedListPayload, UpdateBlockedListPayload } from "app/types";
import { AxiosResponse } from "axios";
import axios from "./config";
import {
  AssignFoodTagsApiResponse,
  CreateServingInstructionIconApiResponse,
  DeleteLocalizedDishAPIResponse,
  FoodTagsGetByIdApiResponse,
  FoodTagsSortApiResponse,
  GetFoodTagsApiResponse,
  GetLocalizedDishApiResponse,
  GetMacroRecipeNutritionalFactsAPIParams,
  GetMacroRecipeNutritionalFactsApiResponse,
  GetRecipeFoodTagApiResponse,
  GetServingInstructionIconsApiResponse,
  GetDishAuditLogsApiResponse,
  LocalizedDishPayload,
  ManageFoodTagsApiResponse,
  PostLocalizedDishAPIResponse,
  UpdateMacroNutritionalFactsAPIParams,
  UpdateRecipeNutritionalFactsApiResponse,
} from "app/types/meals";
import {
  AssignFoodTagsPayload,
  CreateFoodTagsPayload,
  RemoveFoodTagsPayload,
  SortFoodTagPayload,
} from "app/redux/slice/mealsSlice";

export const getDishesAPI = (query?: any) => {
  return axios.get(`/Dishes${query}`);
};

export const getDishesByIdAPI = (dishId: number) => {
  return axios.get(`/Dishes/${dishId}`, {
    headers: { Accept: "application/vnd.lightfields.comp.content.api.json" },
  });
};

export const getCategoriesAPI = (query: any) =>
  axios.get(`/categories${query}`);

export const createDishAPI = (payload: any) => axios.post(`/Dishes`, payload);

export const updateDishAPI = (dishId: number, payload: any) =>
  axios.put(`/Dishes/${dishId}`, payload);

export const createDishLocaleAPI = (dishId: number, payload: any) =>
  axios.post(`/Dishes${dishId}/localizations`, payload);

export const getPortionsAPI = (query: any) => axios.get(`/portions${query}`);

export const createRecipeAPI = (dishId: number, payload: any) =>
  axios.post(`/dishes/${dishId}/Recipes`, payload);

export const updateRecipeAPI = (
  dishId: number,
  recipeId: number,
  payload: any,
) => axios.put(`/dishes/${dishId}/Recipes/${recipeId}`, payload);

export const getDishRecipesAPI = (dishId: number, query?: any) => {
  return axios.get(`/dishes/${dishId}/Recipes${query}`);
};

export const getKitchenSectionAPI = () => {
  return axios.get(`/kitchen-sections`);
};

export const getRecipeAPI = (dishId: number, recipeId?: number) => {
  return axios.get(`/dishes/${dishId}/Recipes/${recipeId}`);
};

export const getItemsAPI = (query: any) => {
  return axios.get(`/items${query}`);
};

export const getSubRecipeAPI = (query: any) => {
  return axios.get(`/subrecipes${query}`);
};

export const getSubRecipeDetailAPI = (subId: number) => {
  return axios.get(`/subrecipes/${subId}`);
};

export const updateSubRecipeAPI = (subId: number, payload: any) => {
  return axios.put(`/subrecipes/${subId}`, payload);
};

export const createSubRecipeAPI = (payload: any) => {
  return axios.post(`/subrecipes`, payload);
};

export const getBlockedListAPI = (storeId: number, query = "") => {
  return axios.get(`/stores/${storeId}/menu-maintenance-schedules${query}`, {
    headers: { Accept: "application/vnd.lightfields.comp.content.api.json" },
  });
};

export const createBlockedListAPI = (
  storeId: number,
  payload: CreateBlockedListPayload,
) => {
  return axios.post(`/stores/${storeId}/menu-maintenance-schedules`, payload);
};

export const updateBlockedListAPI = (
  storeId: number,
  payload: UpdateBlockedListPayload,
) => {
  return axios.put(
    `/stores/${storeId}/menu-maintenance-schedules/${payload.id}`,
    payload,
  );
};
export type UpdateRecipeNutritionalFactsAPIParams = {
  dishId: number;
  recipeId: number;
  payload: {
    fats: number;
    carbohydrate: number;
    proteins: number;
  };
};

export const updateRecipeNutritionalFactsAPI = (
  apiParams: UpdateRecipeNutritionalFactsAPIParams,
): Promise<AxiosResponse<UpdateRecipeNutritionalFactsApiResponse>> => {
  return axios.patch<UpdateRecipeNutritionalFactsApiResponse>(
    `/dishes/${apiParams.dishId}/Recipes/${apiParams.recipeId}/nutritional-facts`,
    apiParams.payload,
  );
};

export const getMacroRecipeNutritionalFactsAPI = (
  apiParams: GetMacroRecipeNutritionalFactsAPIParams,
): Promise<AxiosResponse<GetMacroRecipeNutritionalFactsApiResponse>> => {
  return axios.get<GetMacroRecipeNutritionalFactsApiResponse>(
    `/dishes/${apiParams.dishId}/Recipes/${apiParams.recipeId}/macro-nutritional-facts`,
  );
};

export const updateMacroNutritionalFactsAPI = (
  apiParams: UpdateMacroNutritionalFactsAPIParams,
): Promise<AxiosResponse<GetMacroRecipeNutritionalFactsApiResponse>> => {
  return axios.post<GetMacroRecipeNutritionalFactsApiResponse>(
    `/dishes/${apiParams.dishId}/recipes/${apiParams.recipeId}/macro-nutritional-facts`,
    apiParams.payload,
  );
};

export const getFoodTagsListApi = (): Promise<
  AxiosResponse<GetFoodTagsApiResponse>
> => {
  return axios.get<GetFoodTagsApiResponse>(`/food-tags`);
};

export const createFoodTagsAPI = (
  payload: CreateFoodTagsPayload,
): Promise<AxiosResponse<ManageFoodTagsApiResponse>> => {
  return axios.post<ManageFoodTagsApiResponse>(`/food-tags`, payload);
};

export const getFoodTagsGetByIdApi = (
  id: number,
): Promise<AxiosResponse<FoodTagsGetByIdApiResponse>> => {
  return axios.get<FoodTagsGetByIdApiResponse>(`/food-tags/${id}`);
};
export const updateFoodTagsAPI = (
  payload: CreateFoodTagsPayload,
  id: number,
): Promise<AxiosResponse<ManageFoodTagsApiResponse>> => {
  return axios.put<ManageFoodTagsApiResponse>(`/food-tags/${id}`, payload);
};

export const getRecipeFoodTagsAPI = (
  recipeId: number,
  dishId: number,
): Promise<AxiosResponse<GetRecipeFoodTagApiResponse>> => {
  return axios.get<GetRecipeFoodTagApiResponse>(
    `/dishes/${dishId}/recipes/${recipeId}/tags`,
  );
};
export const sortFoodTagsAPI = (
  recipeId: number,
  dishId: number,
  payload: SortFoodTagPayload,
): Promise<AxiosResponse<FoodTagsSortApiResponse>> => {
  return axios.post<FoodTagsSortApiResponse>(
    `/dishes/${dishId}/recipes/${recipeId}/sort-tags`,
    payload,
  );
};

export const assignFoodTagsAPI = (
  recipeId: number,
  dishId: number,
  payload: AssignFoodTagsPayload,
): Promise<AxiosResponse<AssignFoodTagsApiResponse>> => {
  return axios.post<AssignFoodTagsApiResponse>(
    `/dishes/${dishId}/recipes/${recipeId}/tags`,
    payload,
  );
};

export const removeFoodTagsAPI = (
  recipeId: number,
  dishId: number,
  payload: RemoveFoodTagsPayload,
): Promise<AxiosResponse<AssignFoodTagsApiResponse>> => {
  return axios.post<AssignFoodTagsApiResponse>(
    `/dishes/${dishId}/recipes/${recipeId}/tags`,
    payload,
  );
};

export const syncOdooRecipesAPI = (dishId: number) => {
  return axios.put(`/Dishes/${dishId}/recipes/sync`, {});
};

export const getLocalizedDishAPI = (
  dishId: number,
  query = "",
): Promise<AxiosResponse<GetLocalizedDishApiResponse>> => {
  return axios.get<GetLocalizedDishApiResponse>(
    `/Dishes/${dishId}/localizations${query}`,
  );
};

export const deleteLocalizedDishApi = (
  dishId: number,
  locale: string,
): Promise<AxiosResponse<DeleteLocalizedDishAPIResponse>> =>
  axios.delete<DeleteLocalizedDishAPIResponse>(
    `Dishes/${dishId}/localizations?locale=${locale}`,
  );

export const postLocalizedDishApi = (
  dishId: number,
  payload: LocalizedDishPayload,
): Promise<AxiosResponse<PostLocalizedDishAPIResponse>> =>
  axios.post<PostLocalizedDishAPIResponse>(
    `/Dishes/${dishId}/localizations`,
    payload,
  );
export const putLocalizedDishApi = (
  dishId: number,
  payload: LocalizedDishPayload,
): Promise<AxiosResponse<PostLocalizedDishAPIResponse>> =>
  axios.put<PostLocalizedDishAPIResponse>(
    `/Dishes/${dishId}/localizations`,
    payload,
  );

export const getServingInstructionIconsApi = (): Promise<
  AxiosResponse<GetServingInstructionIconsApiResponse>
> =>
  axios.get<GetServingInstructionIconsApiResponse>(
    `/serving-instruction-icons`,
  );

export const createServingInstructionIconApi = (payload: {
  name: string;
  url?: string;
  icon?: string;
}): Promise<AxiosResponse<CreateServingInstructionIconApiResponse>> =>
  axios.post<CreateServingInstructionIconApiResponse>(
    `/serving-instruction-icons`,
    payload,
  );

export const updateServingInstructionIconApi = (
  id: number,
  payload: { name: string; url?: string; icon?: string },
): Promise<AxiosResponse<CreateServingInstructionIconApiResponse>> =>
  axios.put<CreateServingInstructionIconApiResponse>(
    `/serving-instruction-icons/${id}`,
    payload,
  );

export const getDishAuditLogsAPI = (
  dishId: number,
): Promise<AxiosResponse<GetDishAuditLogsApiResponse>> =>
  axios.get<GetDishAuditLogsApiResponse>(
    `/meals/audit-logs?parentEntityType=Dish&parentEntityId=${dishId}`,
  );
